
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import {
  Lead, LeadStatus, Operation, OperationChild, PermissionCode, User,
} from '@/types';

@Component({
  components: {
  },
})
export default class ResolveChildDialog extends Vue {
  @Prop(Boolean) readonly value!: boolean;
  @Prop(Number) readonly operationId!: number;
  @Prop(Number) readonly childId!: number;

  dialog = false

  loading = false

  resolveOperation = false

  @Watch('value')
  async onValueChange(val: boolean) {
    this.dialog = val;
    if (val === true) {
      this.loading = true;
      this.resolveOperation = false;

      // Seleziona la commessa
      if (this.operationId) {
        await this.$store.dispatch('selectOperation', { id: this.operationId });
      }

      // Seleziona la lead figlia
      if (this.childId) {
        await this.$store.dispatch('selectLead', { id: this.childId });
      }

      this.loading = false;

      // this.$nextTick(() => {
      //   this.refTask = this.$refs.task;
      // });
    } else {
      // setTimeout(() => {
      //   this.$store.dispatch('unselectLead');
      // }, 500);
    }
  }

  @Watch('dialog')
  onDialogChange(val: boolean) {
    if (this.value !== val) {
      this.$emit('input', val);
    }
  }

  get user(): User {
    return this.$store.getters.currentUser;
  }

  get userPermissions(): PermissionCode[] {
    return this.$store.getters.currentUserPermissions;
  }

  get isSuperUser(): boolean {
    return this.user && this.user.isSuperUser;
  }

  // CHILD

  // La figlia selezionata
  get child(): Lead | null {
    return this.$store.getters.currentLead;
  }

  get childStatus(): LeadStatus | null {
    return this.child?.status ?? null;
  }

  get isChildResolved(): boolean {
    return this.childStatus === 'resolved';
  }

  get childTasksCompleted() {
    if (this.child && 'Tasks' in this.child) {
      // eslint-disable-next-line no-restricted-syntax
      for (const task of this.child.Tasks.filter((t) => t.status !== 'disabled')) {
        if (task.status !== 'completed') {
          return false;
        }
      }

      return true;
    }

    return false;
  }

  // Ritorna true se la figlia può essere completata
  get isChildSolvable(): boolean {
    // if (!this.child?.isComplete) return false;

    return this.childTasksCompleted;
  }

  // Ritorna true se è l'unica figlia della commessa a cui appartiene
  get isChildSolo(): boolean {
    return this.operationChildrenIds.length === 1;
  }

  // OPERATION

  // La commessa selezionata
  get operation(): Operation | null {
    return this.$store.getters.currentOperation;
  }

  get operationStatus(): LeadStatus | null {
    return this.operation?.status ?? null;
  }

  get operationChildrenIds(): number[] {
    if (!this.operation
      || !this.operation.childrenIds
      || this.operation.childrenIds.length < 3) return [];

    return this.operation.childrenIds.substring(1, this.operation.childrenIds.length - 1).split(':')
      .map((s: string) => Number(s));
  }

  get operationChildren(): OperationChild[] {
    return this.operation && 'children' in this.operation ? Object.values(this.operation?.children) : [];
  }

  get isOperationResolved(): boolean {
    return this.operationStatus === 'resolved';
  }

  get isOperationSolvable(): boolean {
    return true;
  }

  get sistersStatuses(): LeadStatus[] {
    return this.operationChildren
      .filter((c) => c.id !== this.child?.id)
      .map((c) => c.status);
  }

  // Ritorna true se tutte le sorelle sono completate
  get areSistersResolved(): boolean {
    return !this.sistersStatuses.includes('draft') // accetta solo 'resolved' e 'closed'
      && !this.sistersStatuses.includes('new')
      && !this.sistersStatuses.includes('open')
      && !this.sistersStatuses.includes('pending')
      && !this.sistersStatuses.includes('rejected')
      && !this.sistersStatuses.includes('cancelled');
  }

  get canResolve(): boolean {
    if (!this.isChildResolved && !this.canResolveChild) return false;
    if (this.resolveOperation && !this.canResolveOperation) return false;
    return true;
  }

  // Ritorna true se l'utente può completare la commessa
  get canResolveChild(): boolean {
    if (!this.child
      || 'status' in this.child === false
      || this.child.status !== 'open') {
      return false;
    }

    return this.hasPermission(`leads_${this.child?.type}.resolve`);
  }

  get canResolveOperation(): boolean {
    if (!this.operation
      || 'status' in this.operation === false
      || this.operation.status !== 'open') {
      return false;
    }

    return this.hasPermission(`leads_${this.operation?.type}.resolve`);
  }

  get resolveButtonTitle(): string {
    if (this.isChildResolved) {
      // Questa figlia è risolta
      return 'Completa (commessa)';
    }

    // Questa figlia non è risolta
    return this.resolveOperation ? 'Completa (figlia + commessa)' : 'Completa (solo figlia)';
  }

  get resolveForceTitle(): string {
    // Questa figlia non è risolta
    return this.resolveOperation
      ? 'Forza il completamento della lavorazione (figlia + commessa)'
      : 'Forza il completamento della lavorazione (solo figlia)';
  }

  get resolveButtonEnabled(): boolean {
    if (this.isChildSolo) {
      // Se è la sola figlia...
      if (!this.isChildResolved) {
        // ... e se non è risolta,
        // abilita il tasto solo se è risolvibile
        return this.isChildSolvable;
      }
      // Se invece è già risolta, abilita il tasto solo se:
      // - è richiesto il completamento della commessa
      // - la commessa non è risolta
      // - la commessa è risolvibile
      return this.resolveOperation && !this.isOperationResolved && this.isOperationSolvable;
    }

    // Se NON è la sola figlia...
    if (!this.isChildResolved) {
      // ... e se non è risolta, abilita il tasto solo se:
      // - è risolvibile
      return this.isChildSolvable;
    }
    // Se invece è già risolta, abilita il tasto solo se:
    // - le sorelle sono risolte
    // - è richiesto il completamento della commessa
    // - la commessa non è risolta
    // - la commessa è risolvibile
    return this.areSistersResolved
      && this.resolveOperation && !this.isOperationResolved && this.isOperationSolvable;
  }

  hasPermission(codename: PermissionCode) {
    if (!this.user) return false;
    if (this.isSuperUser) return true;

    return this.userPermissions.includes(codename);
  }

  onCancel() {
    console.log(`${this.constructor.name}: onCancel()`); // eslint-disable-line no-console

    this.dialog = false;
  }

  async onResolve({ force = false }: { force: boolean }) {
    console.log(`${this.constructor.name}: onResolve()`); // eslint-disable-line no-console

    this.loading = true;

    if (!this.isChildResolved) await this.$store.dispatch('resolveLead');
    if (this.resolveOperation) await this.$store.dispatch('resolveOperation');

    this.dialog = false;
    setTimeout(() => {
      this.loading = false;
    }, 500);
  }
}
